import { Building2, User, ExternalLink, ShieldCheck, AlertTriangle, Quote } from "lucide-react";
import { companyLine, companyBadge, personaBadge, sizeBadge } from "./previewModel";

const TONE = {
  teal: "bg-ism-teal/15 text-ism-teal border-ism-teal/30",
  amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  gray: "bg-white/[0.04] text-gray-400 border-white/10",
};

const Badge = ({ b, testId }) => b ? (
  <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border cursor-help ${TONE[b.tone]}`} data-testid={testId} title={b.hint || b.text}>
    {b.tone === "teal" ? <ShieldCheck className="h-3 w-3" /> : b.tone === "amber" ? <AlertTriangle className="h-3 w-3" /> : null}{b.text}
  </span>
) : null;

const UnverifiedNote = ({ prospects }) => prospects.some((p) => !p.verifiedBy) ? (
  <p className="text-[11px] text-amber-300/80 px-1" data-testid="preview-unverified-note">
    <AlertTriangle className="h-3 w-3 inline mr-1" />"Firm unverified" = we only have the company name from a public profile; no registry or company database confirmed it yet. Add industries to your targeting to require verified firms only.
  </p>
) : null;

export { UnverifiedNote };

export const PreviewProspectCard = ({ p, idx, spec, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`preview-prospect-${idx}`}>
    <div className="flex items-start gap-3 px-4 pt-4 pb-3 border-b border-white/[0.06]">
      <div className="h-9 w-9 rounded-lg bg-white/[0.05] flex items-center justify-center flex-shrink-0"><Building2 className="h-4 w-4 text-gray-300" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-white text-sm font-medium truncate" data-testid={`preview-company-${idx}`}>{p.companyName || "Firm not disclosed"}</p>
          {p.icpMatch?.score != null && <span className="text-xs text-ism-teal" data-testid={`preview-fit-${idx}`}>{p.icpMatch.score}% fit</span>}
        </div>
        {companyLine(p) && <p className="text-xs text-gray-400 truncate" data-testid={`preview-company-line-${idx}`}>{companyLine(p)}</p>}
        <div className="flex flex-wrap gap-1.5 mt-2">
          <Badge b={companyBadge(p)} testId={`preview-company-badge-${idx}`} />
          <Badge b={sizeBadge(p, spec)} testId={`preview-size-badge-${idx}`} />
        </div>
      </div>
    </div>
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="h-9 w-9 rounded-full bg-ism-teal/15 flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-ism-teal" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm truncate" data-testid={`preview-person-${idx}`}>{p.personName || "Decision-maker to be identified in the full hunt"}</p>
        {p.personTitle && <p className="text-xs text-gray-400 truncate">{p.personTitle}</p>}
        <div className="flex flex-wrap gap-1.5 mt-2"><Badge b={personaBadge(p)} testId={`preview-persona-badge-${idx}`} /></div>
        {(p.whyGoodFit || []).slice(0, 2).map((w) => <p key={w} className="text-xs text-gray-500 mt-1.5">• {w}</p>)}
        {(p.evidence || []).filter((e) => e.quote).slice(0, 1).map((e) => (
          <p key={e.quote} className="text-[11px] text-gray-500 mt-1.5 flex items-start gap-1 italic"><Quote className="h-3 w-3 mt-0.5 flex-shrink-0" />{e.quote}</p>
        ))}
        {(p.alsoAt || []).length > 0 && (
          <p className="text-[11px] text-gray-500 mt-1.5" data-testid={`preview-also-at-${idx}`}>
            +{p.alsoAt.length} more buyer{p.alsoAt.length > 1 ? "s" : ""} found here ({p.alsoAt.map((a) => a.personTitle || a.personName).filter(Boolean).slice(0, 2).join(", ")}) — kept for the full hunt so this preview covers different firms
          </p>
        )}
        <div className="flex items-center gap-3 mt-2">
          {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-ism-teal/80 hover:text-ism-teal flex items-center gap-1" data-testid={`preview-profile-link-${idx}`}><ExternalLink className="h-3 w-3" /> Profile</a>}
          {children}
        </div>
      </div>
    </div>
  </div>
);
